import { useState } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";

import { applyUnderline, renderWithBlanks } from "../../../../../utils/textUtils";
import { typeInfo } from "../../../../../constants/typeInfo";

import CommonPage from "../../common/CommonPage";
import PassagelessPromptBox, { isPassagelessPage } from "../PassagelessPromptBox";

const MultipleChoicePage = ({
  section,
  pageList,
  currentPage,
  currentIndex,
  setPageList,
  handlePreviousPage,
  handleNextPage,
}) => {
  const dispatch = useDispatch();

  const [overlayVisible, setOverlayVisible] = useState(false);

  const selectOption = ({ optionIndex }) => {
    const realIndex = optionIndex + 1;
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        let inputList = page.input;
        if (inputList.includes(realIndex)) {
          inputList = inputList.filter((input) => input !== realIndex);
        } else {
          inputList = [...inputList, realIndex];
        }
        return {
          ...page,
          input: inputList,
        };
      }
      return page;
    });
    setPageList(newPageList);
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;
    const isCorrect =
      currentPage.answer.length === currentPage.input.length &&
      currentPage.input.every((input) => currentPage.answer.includes(input));
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          isCorrect,
          isSubmitted: true,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setOverlayVisible(true);
    setTimeout(() => {
      setOverlayVisible(false);
    }, 600);
  };

  // [모의고사 리디자인] 코드베이스 관례(Math.min(window.innerWidth, window.screen.width) <= 768)로 모바일 판정
  const isMobileW = Math.min(window.innerWidth, window.screen.width) <= 768;

  // 지문 없는 문항은 '전체 본문' 칸을 띄우지 않고, text(<보기> 등)를 발문 아래 상자로 보인다
  const isPassageless = isPassagelessPage(currentPage);

  return (
    <CommonPage
      currentIndex={currentIndex}
      currentPage={currentPage}
      handlePreviousPage={handlePreviousPage}
      handleNextPage={handleNextPage}
      totalNo={pageList.length}
      paragraph={isPassageless ? null : currentPage.text}
      accentColor={typeInfo.paragraph.color}
      overlayVisible={overlayVisible}
      isCorrect={currentPage.isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: isMobileW ? "16px" : "22px",
          // [여백 수술] 폰은 화면 기준 폭 — 조상 요소가 좁혀도 면역
          width: isMobileW ? `${Math.min(window.innerWidth, window.screen.width) - 56}px` : "100%",
          maxWidth: "680px", // [모의고사 리디자인] 전폭 75vw 바 → 중앙 열
          margin: "0 auto",
        }}
      >
      {/* [모의고사 리디자인] Q칩 + 카운터 + 진행바 — 어법과 동일 문법, 색만 모의고사 토큰 */}
      <div style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <span style={{ backgroundColor: "#0E141B", color: "#FFF", borderRadius: "8px", fontSize: isMobileW ? "12px" : "13px", fontWeight: 800, padding: "2px 10px" }}>
            Q{currentIndex + 1}
          </span>
          <span style={{ marginLeft: "auto", fontSize: isMobileW ? "12px" : "13px", color: "#7A8490", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {currentIndex + 1} / {pageList.length}
          </span>
        </div>
        <div style={{ height: "4px", borderRadius: "999px", backgroundColor: "#EDF2F1", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${((currentIndex + 1) / Math.max(pageList.length, 1)) * 100}%`, backgroundColor: typeInfo.paragraph.color, borderRadius: "999px", transition: "width .25s" }} />
        </div>
      </div>
        <div
          style={{
            fontSize: isMobileW ? "16px" : "19px", // [폰트 통일] 질문 반 단계 다운
            lineHeight: 1.75,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            wordBreak: "keep-all",
          }}
        >
          {/* 번호는 Q칩으로 분리 · 질문 속 --**(A)**-- 마커는 밑줄 빈칸 칩으로("밤티" 수술) */}
          {renderWithBlanks(
            currentPage.question.replace(
              "의 개수를 적으시오.",
              "을 모두 고르시오."
            )
          )}
        </div>
        {isPassageless && <PassagelessPromptBox text={currentPage.text} isMobile={isMobileW} />}
        {!currentPage.isSubmitted && (
          <div
            style={{
              fontSize: "15px",
              fontWeight: 800,
              color: "#00A396",
            }}
          >
            {(currentPage.input || []).length}개 선택됨 — 해당하는 것을 모두
            고른 뒤 제출하세요
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            // [모의고사 리디자인] 모바일은 바깥(공용틀)이 스크롤하므로 내부 캡 제거
            maxHeight: isMobileW ? "none" : "48vh",
            overflowY: isMobileW ? "visible" : "auto",
          }}
        >
          {currentPage.choices.map((option, optionIndex) => {
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%", // [모의고사 리디자인] 75vw → 열 전폭
                  boxSizing: "border-box",
                  padding: isMobileW ? "12px 13px" : "14px 18px",
                  // 채점 상태별 보더: 정답 teal / 오답 red / 선택 모의고사 red-soft
                  border: currentPage.isSubmitted
                    ? currentPage.answer.includes(optionIndex + 1)
                      ? "1.5px solid #00D9C0"
                      : currentPage.input.includes(optionIndex + 1)
                      ? "1.5px solid #C2452D"
                      : "1px solid #E6EDEC"
                    : currentPage.input.includes(optionIndex + 1)
                    ? `1.5px solid ${typeInfo.paragraph.color}`
                    : "1px solid #E6EDEC",
                  borderRadius: "12px",
                  fontWeight:
                    currentPage.input.includes(optionIndex + 1) ||
                    (currentPage.isSubmitted &&
                      currentPage.answer.includes(optionIndex + 1))
                      ? "bold"
                      : "normal",
                  cursor: !currentPage.isSubmitted && "pointer",
                  // [모의고사 리디자인] 기존 조건 분기 그대로, 색만 ECHO 팔레트:
                  // 채점 후 — 정답 #E5FBF8/teal(못 고른 정답은 red 글자로 표시) ·
                  // 잘못 고른 보기 #FCEDE9/red. 채점 전 선택 — 모의고사 토큰.
                  backgroundColor: currentPage.isSubmitted
                    ? currentPage.answer.includes(optionIndex + 1)
                      ? "#E5FBF8"
                      : currentPage.input.includes(optionIndex + 1) &&
                        "#FCEDE9"
                    : currentPage.input.includes(optionIndex + 1) &&
                      typeInfo["paragraph"].backgroundColor,
                  color: currentPage.isSubmitted
                    ? currentPage.answer.includes(optionIndex + 1)
                      ? currentPage.input.includes(optionIndex + 1)
                        ? "#00A396"
                        : "#C2452D"
                      : currentPage.input.includes(optionIndex + 1) && "#C2452D"
                    : currentPage.input.includes(optionIndex + 1) &&
                      typeInfo["paragraph"].color,
                  fontSize: isMobileW ? "15px" : "16.5px", // [폰트 통일]
                  fontWeight: 500, // [폰트 통일] 기본 400 → 중간 굵기, 질문과 한 세트
                  letterSpacing: "-0.01em",
                  lineHeight: 1.65,
                  wordBreak: "keep-all",
                }}
                onClick={() => {
                  if (currentPage.isSubmitted) return;
                  selectOption({ optionIndex });
                }}
              >
                <span>{applyUnderline(option)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </CommonPage>
  );
};

export default MultipleChoicePage;
